<script lang="ts">
import { defineComponent } from "vue";
import { apiService } from "@/services/services";
import { Nullable } from "@/models/nullable.type";
import LanguagePicker from "@/shared/components/LanguagePicker.vue";
import config from "@/config";
import { getVersionId } from "@/shared/utils/misc/version";
import { useBrowse } from "@/shared/mixins/useLink";
import { Capacitor } from "@capacitor/core";
import LoadingOverlay from "@/shared/components/LoadingOverlay.vue";
import i18n from "@/core/i18n";
import KlereoPasswordInput from "@/shared/components/KlereoPasswordInput.vue";
import { variant } from "@/core/variant.ts";

export default defineComponent({
  components: { KlereoPasswordInput, LoadingOverlay, LanguagePicker },
  data() {
    return {
      loading: false,
      login: localStorage.getItem("kwa-login") ?? "",
      password: localStorage.getItem("kwa-password") ?? "",
      error: undefined as Nullable<unknown>,
      version: getVersionId(),
      isNativePlatform: Capacitor.isNativePlatform(),
      externalLinksRoot: config.externalLinksRoot,
      variant,
    };
  },
  computed: {
    locale() {
      return i18n.global.locale;
    },
  },
  methods: {
    submit: async function () {
      try {
        this.loading = true;
        const token = await apiService.login(this.login, this.password);
        localStorage.setItem("kwa-login", this.login);
        localStorage.setItem("kwa-password", this.password);
        localStorage.setItem("token", token);
        await this.$router.push(history.state.redirectTo || "/");
      } catch (err: unknown) {
        console.error(err);
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
    browse: useBrowse(),
  },
});
</script>

<template>
  <div
    class="w-full m-5 md:w-[350px] md:m-auto self-center safe-area-inset-padding-top"
  >
    <loading-overlay :loading="loading" :error="error" />

    <div class="absolute text-xs bottom-0 right-0 text-gray-500 p-2">
      v{{ version }}
    </div>
    <div class="flex justify-center mb-5">
      <img src="@/assets/images/logo.svg" alt="Klereo" class="md:w-full" />
    </div>
    <p class="text-white text-center font-medium mb-5">
      {{ $t("WelcomeToKlereoConnect") }}
      <br />
      {{ $t("PleaseLogIn") }}
    </p>
    <div>
      <form class="card p-3 mb-5" v-on:submit.prevent="submit">
        <div class="mb-4">
          <label class="block form-label" for="username">
            {{ $t("Login") }}
          </label>
          <input
            class="form-input"
            id="username"
            v-model="login"
            type="text"
            :placeholder="$t('Login')"
          />
        </div>

        <div class="mb-8">
          <label class="block form-label" for="password">
            {{ $t("Pass") }}
          </label>
          <klereo-password-input
            class="form-input"
            v-model="password"
            id="password"
            type="password"
            :placeholder="$t('Pass')"
          />
          <div class="mt-3 text-right text-xs cursor-pointer">
            <a
              @click="
                browse({ url: externalLinksRoot + '/v1/?page=forgot-password' })
              "
            >
              {{ $t("Forget") }}
            </a>
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-center">
            <button class="btn--primary w-full" type="submit">
              {{ $t("Connect") }}
            </button>
          </div>
          <div class="w-full text-center" v-if="isNativePlatform">
            <a class="btn--secondary" type="submit" href="/ble">
              {{ $t("BleBluetoothLink") }}
            </a>
          </div>
        </div>
      </form>
      <div
        class="text-right text-white text-[10px] font-bold uppercase mr-5 cursor-pointer"
      >
        <a @click="browse({ url: externalLinksRoot + '/v1/?page=register' })">
          {{ $t("NewCnt") }} </a
        ><br /><br />
        <a
          v-if="!isNativePlatform"
          @click="browse({ url: externalLinksRoot + '/pro' })"
        >
          {{ $t("ConnectPro") }}
        </a>
      </div>
    </div>

    <div class="mt-20 flex justify-center">
      <language-picker />
    </div>

    <div class="text-center mt-5">
      <a
        className="text-white cursor-pointer text-sm"
        @click="
          browse({
            url:
              externalLinksRoot +
              `/docs/ConditionGeneraleServiceKlereoV2-${locale}.htm`,
          })
        "
      >
        {{ $t("TermsAndConditionsLink") }}
      </a>
    </div>

    <div class="text-center">
      <a
        className="text-white cursor-pointer text-sm"
        @click="
          browse({
            url: externalLinksRoot + `/docs/PrivacyPolicy-${locale}.htm`,
          })
        "
      >
        {{ $t("PrivacyPolicyLink") }}
      </a>
    </div>

    <div
      class="mt-20 justify-center flex items-center gap-3"
      v-if="!isNativePlatform"
    >
      <a :href="variant.storeLinks.playStore"
        ><img
          style="width: 160px"
          :alt="$t('PlayStoreImageAltText')"
          src="https://play.google.com/intl/en_us/badges/static/images/badges/fr_badge_web_generic.png"
      /></a>
      <a :href="variant.storeLinks.appStore">
        <img
          style="width: 150px"
          src="/img/stores/appstore_blk.svg"
          :alt="$t('AppStoreImageAltText')"
        />
      </a>
    </div>
  </div>
</template>
